/**
 * Draws a person's Projects and Bundles as a tree: purpose, leading value, open activities and,
 * on a previewed proposal, what each node would change. Used by the Values page and by Flux
 * Agent's structure card while it sets up or tidies a structure. Pure presentation; the payload
 * is `StructureTree` from `GET /api/workspace/structure` or a preview.
 *
 * @module apps/web/src/components/values/StructureTreeView
 */
import type { ReactNode } from 'react';
import { Folder, FolderOpen, Layers, Package } from 'lucide-react';
import type {
  BundleNode,
  NodeChange,
  ProjectNode,
  StructureFinding,
  StructureTree,
} from '@fluxability/types';

/** Short label and tone for a change marker. */
function changeLabel(change: NodeChange, names: Map<string, string>): [string, string] {
  switch (change.kind) {
    case 'new':
      return [
        'New',
        'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
      ];
    case 'renamed':
      return [
        `Was "${change.from}"`,
        'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200',
      ];
    case 'purpose':
      return ['Purpose', 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200'];
    case 'value':
      return ['Value', 'bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200'];
    case 'moved':
      return [
        change.from ? `Moved from ${names.get(change.from) ?? 'another Project'}` : 'Moved in',
        'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
      ];
    case 'merged_into':
      return [
        `Merged into ${names.get(change.into) ?? 'another Bundle'}`,
        'bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200',
      ];
    case 'archived':
      return ['Archived', 'bg-muted text-muted-foreground'];
  }
}

/** Change markers for one node. */
function Changes({
  changes,
  names,
}: {
  /** The node's change markers. */
  changes?: NodeChange[];
  /** Node id to name, for "moved from" and "merged into". */
  names: Map<string, string>;
}) {
  if (!changes?.length) return null;
  return (
    <span className="flex flex-wrap gap-1">
      {changes.map((change, index) => {
        const [label, tone] = changeLabel(change, names);
        return (
          <span key={index} className={`rounded px-1.5 py-0.5 text-[11px] font-medium ${tone}`}>
            {label}
          </span>
        );
      })}
    </span>
  );
}

/** Props for {@link StructureTreeView}. */
export interface StructureTreeViewProps {
  /** The tree to draw. */
  tree: StructureTree;
  /** Findings to list under the tree, if any. */
  findings?: StructureFinding[];
  /** Optional control drawn at the end of each Project or Bundle row, such as a value picker. */
  renderControl?: (kind: 'project' | 'bundle', node: ProjectNode | BundleNode) => ReactNode;
  /** Smaller type for chat cards. */
  compact?: boolean;
}

/** One Bundle row. */
function BundleRow({
  bundle,
  props,
  names,
}: {
  /** The Bundle. */
  bundle: BundleNode;
  /** The tree view's props, for value names and controls. */
  props: StructureTreeViewProps;
  /** Node id to name. */
  names: Map<string, string>;
}) {
  const value = bundle.value_id ? props.tree.value_names?.[bundle.value_id] : undefined;
  return (
    <li
      className={`flex flex-col gap-0.5 rounded-md px-2 py-1.5 ${bundle.archived ? 'opacity-60' : ''}`}
      data-testid={`structure-bundle-${bundle.id}`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <Package className="text-muted-foreground h-3.5 w-3.5 shrink-0" aria-hidden />
        <span className={`font-medium ${bundle.archived ? 'line-through' : ''}`}>
          {bundle.name}
        </span>
        <span className="text-muted-foreground text-xs">{bundle.open_activity_count} open</span>
        {value && (
          <span className="rounded-full border px-2 py-0.5 text-[11px]" title="Value">
            {value}
          </span>
        )}
        <Changes changes={bundle.changes} names={names} />
        {props.renderControl?.('bundle', bundle)}
      </div>
      <p className="text-muted-foreground pl-5 text-xs">
        {bundle.purpose || <em>No purpose yet</em>}
      </p>
    </li>
  );
}

/** One Project with its Bundles. */
function ProjectRow({
  project,
  props,
  names,
}: {
  /** The Project. */
  project: ProjectNode;
  /** The tree view's props, for value names and controls. */
  props: StructureTreeViewProps;
  /** Node id to name. */
  names: Map<string, string>;
}) {
  const value = project.value_id ? props.tree.value_names?.[project.value_id] : undefined;
  const Icon = project.bundles.length ? FolderOpen : Folder;
  return (
    <li
      className={project.archived ? 'opacity-60' : ''}
      data-testid={`structure-project-${project.id}`}
    >
      <div className="flex flex-wrap items-center gap-2 px-1 py-1">
        <Icon className="text-primary h-4 w-4 shrink-0" aria-hidden />
        <span className={`font-semibold ${project.archived ? 'line-through' : ''}`}>
          {project.name}
        </span>
        {value && (
          <span
            className="bg-primary/10 text-primary rounded-full px-2 py-0.5 text-[11px]"
            title="Leading value"
          >
            {value}
          </span>
        )}
        <Changes changes={project.changes} names={names} />
        {props.renderControl?.('project', project)}
      </div>
      <p className="text-muted-foreground pl-7 text-xs">
        {project.purpose || <em>No purpose yet</em>}
      </p>
      {project.bundles.length > 0 && (
        <ul className="ml-3 mt-1 border-l pl-3" role="group" aria-label={`${project.name} Bundles`}>
          {project.bundles.map(bundle => (
            <BundleRow key={bundle.id} bundle={bundle} props={props} names={names} />
          ))}
        </ul>
      )}
    </li>
  );
}

/** Draw the Project/Bundle tree, then any findings. */
export function StructureTreeView(props: StructureTreeViewProps) {
  const { tree, findings, compact } = props;
  const names = new Map<string, string>();
  for (const project of tree.projects) {
    names.set(project.id, project.name);
    for (const bundle of project.bundles) names.set(bundle.id, bundle.name);
  }
  for (const bundle of tree.loose_bundles) names.set(bundle.id, bundle.name);
  const empty = !tree.projects.length && !tree.loose_bundles.length;
  return (
    <div
      className={compact ? 'space-y-2 text-xs' : 'space-y-3 text-sm'}
      data-testid="structure-tree"
    >
      {empty ? (
        <p className="text-muted-foreground">No Projects or Bundles yet.</p>
      ) : (
        <ul className="space-y-2" aria-label="Projects and Bundles">
          {tree.projects.map(project => (
            <ProjectRow key={project.id} project={project} props={props} names={names} />
          ))}
          {tree.loose_bundles.length > 0 && (
            <li>
              <div className="text-muted-foreground flex items-center gap-2 px-1 py-1">
                <Layers className="h-4 w-4" aria-hidden />
                <span className="font-medium">Not in a Project</span>
              </div>
              <ul className="ml-3 border-l pl-3" role="group" aria-label="Bundles not in a Project">
                {tree.loose_bundles.map(bundle => (
                  <BundleRow key={bundle.id} bundle={bundle} props={props} names={names} />
                ))}
              </ul>
            </li>
          )}
        </ul>
      )}
      {tree.unbundled_activities > 0 && (
        <p className="text-muted-foreground text-xs">
          {tree.unbundled_activities} open activities are not in any Bundle.
        </p>
      )}
      {findings && findings.length > 0 && (
        <div className="rounded-md border border-dashed p-2">
          <p className="mb-1 text-xs font-medium">Worth a look</p>
          <ul className="text-muted-foreground list-disc space-y-0.5 pl-4 text-xs">
            {findings.map((finding, index) => (
              <li key={`${finding.kind}-${finding.node_id ?? index}`}>{finding.detail}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
